import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getComplianceRegister, getConfirmationQueue, getOrgDashboard, type OrgDashboard } from '../../api/client';
import { Icon } from '../../components/Icon';
import { Stat } from './shared';

interface Exception {
  label: string; count: number; to: string; detail: string;
  severity: 'critical' | 'high' | 'medium';
}

/**
 * Built around exceptions rather than totals. A count that needs no action is
 * background; a count that blocks something is the point of the screen.
 */
export function ExecutiveDashboard() {
  const [data, setData] = useState<OrgDashboard | null>(null);
  const [identityGaps, setIdentityGaps] = useState(0);
  const [complianceOpen, setComplianceOpen] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getOrgDashboard(),
      getConfirmationQueue().catch(() => null),
      getComplianceRegister().catch(() => null),
    ])
      .then(([d, queue, compliance]) => {
        setData(d);
        setIdentityGaps(queue?.legalIdentity && queue.legalIdentity.status !== 'confirmed'
          ? queue.legalIdentity.missingFields.length : 0);
        setComplianceOpen(compliance?.items.filter((i) => i.status !== 'complete').length ?? 0);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const exceptions: Exception[] = data ? ([
    { label: 'Legal identity fields unsupplied', count: identityGaps, to: '/org/compliance', severity: 'critical', detail: 'Blocks compliance filing and any document issued in the company name' },
    { label: 'Compliance items open', count: complianceOpen, to: '/org/compliance', severity: 'high', detail: 'Applicability unassessed or action outstanding' },
    { label: 'Employees with no reporting manager', count: data.organization.withoutManager, to: '/org/confirmation-queue', severity: 'critical', detail: 'Approval routing cannot resolve without one' },
    { label: 'Employees with no designation', count: data.organization.withoutDesignation, to: '/org/confirmation-queue', severity: 'critical', detail: 'A job description cannot describe an unnamed position' },
    { label: 'Approval requests with no resolved approver', count: data.approvals.unrouted, to: '/org/approvals', severity: 'critical', detail: 'Requests that cannot move' },
    { label: 'Employees with no job description', count: data.organization.withoutJobDescription, to: '/org/job-descriptions', severity: 'high', detail: 'Responsibilities and limits are undocumented' },
    { label: 'Work arrangements unconfirmed', count: data.people.workArrangementPending, to: '/org/work-arrangements', severity: 'high', detail: 'Office, remote or hybrid is unknown' },
    { label: 'Work-from-home requests open', count: data.people.wfhRequestsOpen, to: '/org/work-arrangements', severity: 'high', detail: 'Awaiting a decision' },
    { label: 'Approvals pending', count: data.approvals.pending, to: '/org/approvals', severity: 'medium', detail: 'In flight' },
    { label: 'Delegations expiring within 30 days', count: data.authority.expiringWithin30Days, to: '/org/delegations', severity: 'medium', detail: 'Authority about to lapse' },
    { label: 'Employees with no department', count: data.organization.withoutDepartment, to: '/org/confirmation-queue', severity: 'medium', detail: 'Reporting and filtering are incomplete' },
  ] as Exception[]).filter((e) => e.count > 0) : [];

  return (
    <>
      <div className="page-head">
        <div>
          <span className="eyebrow"><Icon name="dashboard" size="xs" /><span>Executive</span></span>
          <h1>Organizational Dashboard</h1>
        </div>
      </div>

      {error && <div className="banner error" style={{ marginBottom: 14 }}>{error}</div>}
      {loading && <div className="panel"><div className="empty"><strong>Loading…</strong></div></div>}

      {data && (
        <>
          <div className="panel" style={{ marginBottom: 16 }}>
            <div className="panel-head">
              <strong>Requires attention</strong>
              <span className="muted" style={{ fontSize: 12 }}>
                {exceptions.length === 0 ? 'Nothing outstanding' : `${exceptions.length} item${exceptions.length === 1 ? '' : 's'}`}
              </span>
            </div>
            {exceptions.length === 0 ? (
              <div className="empty">
                <strong>No organizational exceptions</strong>
                <p>Every employee has a manager, a designation, a job description and a confirmed work arrangement.</p>
              </div>
            ) : (
              <ul className="org-exceptions">
                {exceptions.map((e) => (
                  <li key={e.label} className={`org-exception org-exception-${e.severity}`}>
                    <span className="org-exception-count">{e.count}</span>
                    <span className="org-exception-body">
                      <Link className="record-link" to={e.to}>{e.label}</Link>
                      <span className="muted">{e.detail}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <h2 className="org-section-title">Workforce</h2>
          <div className="org-stats">
            <Stat label="On record" value={data.people.total} />
            <Stat label="Active" value={data.people.active} />
            <Stat label="Office" value={data.people.office} />
            <Stat label="Remote" value={data.people.remote} />
            <Stat label="Hybrid" value={data.people.hybrid} />
            <Stat label="Interns" value={data.people.interns} />
            <Stat label="Former employees" value={data.people.formerEmployees} />
          </div>

          <h2 className="org-section-title">Approvals</h2>
          <div className="org-stats">
            <Stat label="Pending" value={data.approvals.pending} tone={data.approvals.pending ? 'warn' : 'ok'} />
            <Stat label="Approved" value={data.approvals.approved} />
            <Stat label="Rejected" value={data.approvals.rejected} />
            <Stat label="Escalated" value={data.approvals.escalated} tone={data.approvals.escalated ? 'bad' : undefined} />
            <Stat label="Unrouted" value={data.approvals.unrouted} tone={data.approvals.unrouted ? 'bad' : 'ok'} />
          </div>

          <h2 className="org-section-title">Delegated authority</h2>
          <div className="org-stats">
            <Stat label="Active delegations" value={data.authority.activeDelegations} />
            <Stat label="Expiring in 30 days" value={data.authority.expiringWithin30Days} tone={data.authority.expiringWithin30Days ? 'warn' : undefined} />
          </div>

          <p className="muted org-footnote" style={{ marginTop: 18 }}>
            Leave, attendance, recruitment, expenses and travel figures are owned by their own modules and are not
            duplicated here. This dashboard covers people, organization and authority.
          </p>
        </>
      )}
    </>
  );
}
